import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import apiClient from '../../networking/axiosconfig';
import { useSelector } from 'react-redux';

const BannerDialog = ({ 
  open, 
  onClose, 
  onBannerAdded, 
  onBannerUpdated, 
  initialBanner 
}) => {
  const { data: config } = useSelector((state) => state.config);
  const [formData, setFormData] = useState({
    title: '',
    image: null,
    link: '',
    target: '_blank',
    isActive: true
  });
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialBanner) {
      setFormData({
        title: initialBanner.title || '',
        image: initialBanner.image || null,
        link: initialBanner.link || '',
        target: initialBanner.target || '_blank',
        isActive: initialBanner.isActive !== undefined ? initialBanner.isActive : true
      });
      if (initialBanner.image) {
        const imageUrl = initialBanner.image.startsWith('http') 
          ? initialBanner.image 
          : `${config.API_BASE_URL}${initialBanner.image.startsWith('/') ? '' : '/'}${initialBanner.image}`;
        setPreviewUrl(imageUrl);
      } else {
        setPreviewUrl('');
      }
    } else {
      setFormData({
        title: '',
        image: null,
        link: '',
        target: '_blank',
        isActive: true
      });
      setPreviewUrl('');
    }
  }, [initialBanner, config.API_BASE_URL, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));
      setPreviewUrl(URL.createObjectURL(file));
    }
    // Reset file input to allow selecting the same file again
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('link', formData.link);
      formDataToSend.append('target', formData.target);
      formDataToSend.append('isActive', formData.isActive);
      
      if (formData.image instanceof File) {
        formDataToSend.append('image', formData.image);
      }

      if (initialBanner) {
        // Update existing banner
        await apiClient.put(
          `${config.API_BASE_URL}/api/banners/${initialBanner._id}`,
          formDataToSend,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        if (onBannerUpdated) onBannerUpdated();
      } else {
        // Create new banner
        await apiClient.post(
          `${config.API_BASE_URL}/api/banners`,
          formDataToSend,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        if (onBannerAdded) onBannerAdded();
      }
      onClose();
    } catch (error) {
      console.error('Error saving banner:', error);
      setError(error.response?.data?.message || 'Failed to save banner');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialBanner ? 'Edit Banner' : 'Add New Banner'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />

            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="banner-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="banner-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Upload Banner Image
                </Button>
              </label>
              {(formData.image || previewUrl) && (
                <Box mt={2} textAlign="center">
                  <img
                    src={previewUrl}
                    alt="Banner Preview"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '200px',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/400x200?text=Image+Not+Found';
                    }}
                  />
                  <Typography variant="caption" display="block" gutterBottom>
                    {formData.image?.name || 'Current Image'}
                  </Typography>
                </Box>
              )}
            </Box>

            <TextField
              label="Link URL"
              name="link"
              value={formData.link}
              onChange={handleChange}
              fullWidth
              margin="normal"
              placeholder="https://example.com"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Link Target</InputLabel>
              <Select
                name="target"
                value={formData.target}
                onChange={handleChange}
                label="Link Target"
              >
                <MenuItem value="_blank">Open in new tab</MenuItem>
                <MenuItem value="_self">Open in same tab</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                name="isActive"
                value={formData.isActive}
                onChange={(e) => 
                  setFormData(prev => ({
                    ...prev,
                    isActive: e.target.value === 'true'
                  }))
                }
                label="Status"
              >
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </Select>
            </FormControl>

            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : initialBanner ? (
              'Update Banner'
            ) : (
              'Add Banner'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BannerDialog;
